import { toast } from 'react-toastify';

import { getAiSpendRun } from '../../services/aiSpendService';
import {
  formatInstant,
  formatLatency,
  formatMoneyPrecise,
  formatUsage,
  isBlank,
  PRICING_STATUS_LABELS,
} from '../../utils/helpers/aiSpendFormat';
import useApiResource from '../../utils/hooks/useApiResource';
import CustomDialog from '../modals/CustomDialog/CustomDialog';

/**
 * `0` is a value, not missing data.
 *
 * The `{value || '—'}` idiom renders "Latency: 0 ms" and "Images: 0" as "no
 * data", which is exactly the kind of small lie that makes people stop
 * trusting a dashboard.
 */
function FieldRow({ label, value }) {
  return (
    <div className="flex justify-between gap-4 border-b border-slate-100 py-1.5 text-sm last:border-0 dark:border-slate-700">
      <span className="text-slate-500 dark:text-slate-400">{label}</span>
      <span className="text-right font-medium text-slate-900 dark:text-white">
        {isBlank(value) ? '—' : value}
      </span>
    </div>
  );
}

export default function RunDetailModal({ runUuid, onClose, timezone }) {
  const { data, error, loading } = useApiResource(
    ({ signal }) => getAiSpendRun({ runUuid, signal }),
    { deps: [runUuid || ''], enabled: Boolean(runUuid) },
  );

  if (error) toast.error(error);

  const rates = data?.price_snapshot?.rates || {};

  return (
    <CustomDialog
      isOpen={Boolean(runUuid)}
      closeModal={onClose}
      headerText="Call detail"
      panelClassName="max-w-2xl"
    >
      {loading && <p className="text-sm text-slate-500">Loading…</p>}
      {data && (
        <div className="space-y-5">
          <section>
            <FieldRow label="When" value={formatInstant(data.created, timezone)} />
            <FieldRow label="Provider" value={data.provider} />
            <FieldRow label="Model requested" value={data.model} />
            <FieldRow label="Model served" value={data.provider_model} />
            <FieldRow label="Feature" value={data.feature_label} />
            <FieldRow label="Usage" value={formatUsage(data.usage)} />
            <FieldRow label="How measured" value={data.measurement} />
            <FieldRow label="Latency" value={formatLatency(data.latency_ms)} />
            <FieldRow label="Attempt" value={`${data.attempt} (${data.attempt_kind})`} />
            <FieldRow label="HTTP status" value={data.http_status} />
            <FieldRow label="Provider succeeded" value={data.success ? 'Yes' : 'No'} />
            <FieldRow label="Response usable" value={data.usable ? 'Yes' : 'No'} />
            <FieldRow label="Student" value={data.subject_email} />
            <FieldRow label="Triggered by" value={data.initiator_email} />
            <FieldRow label="Organization" value={data.organization} />
          </section>

          <section>
            <h4 className="mb-1 text-sm font-semibold text-slate-900 dark:text-white">
              How this cost was worked out
            </h4>
            <FieldRow
              label="Cost"
              value={
                data.cost_usd === null
                  ? 'Unknown'
                  : formatMoneyPrecise(data.cost_usd)
              }
            />
            <FieldRow
              label="Basis"
              value={PRICING_STATUS_LABELS[data.pricing_status] || data.pricing_status}
            />
            <FieldRow
              label="Rate in force"
              value={data.price_snapshot?.effective_from?.slice(0, 10)}
            />
            <FieldRow
              label="Input / 1M"
              value={rates.input_per_million_micro_usd}
            />
            <FieldRow
              label="Cached input / 1M"
              value={rates.cached_input_per_million_micro_usd}
            />
            <FieldRow
              label="Output / 1M"
              value={rates.output_per_million_micro_usd}
            />
            <FieldRow label="Per unit" value={rates.per_unit_micro_usd} />
          </section>

          {data.error_class && (
            <section>
              <h4 className="mb-1 text-sm font-semibold text-red-700 dark:text-red-300">
                {data.error_class}
              </h4>
              <pre className="max-h-40 overflow-auto rounded-lg bg-slate-100 p-3 text-xs text-slate-700 dark:bg-slate-900 dark:text-slate-200">
                {data.error_message || '(no message)'}
              </pre>
            </section>
          )}

          {data.siblings?.length > 0 && (
            <section>
              <h4 className="mb-1 text-sm font-semibold text-slate-900 dark:text-white">
                Other attempts of the same operation
              </h4>
              <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">
                Retries, transport fallbacks and correction passes each cost money
                separately.
              </p>
              <ul className="space-y-1 text-sm">
                {data.siblings.map((sibling) => (
                  <li key={sibling.uuid} className="flex justify-between gap-3">
                    <span className="text-slate-500">
                      #{sibling.attempt} · {sibling.attempt_kind}
                    </span>
                    <span className="text-slate-900 dark:text-white">
                      {sibling.cost_usd === null
                        ? 'cost unknown'
                        : formatMoneyPrecise(sibling.cost_usd)}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </CustomDialog>
  );
}
